import { Combobox, Text } from "@sentricmusic/ui";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useTranslation } from "react-i18next";
import {
  FaArrowAltCircleLeft,
  FaBroadcastTower,
  FaChartLine,
  FaCog,
  FaCookie,
  FaDatabase,
  FaFileAlt,
  FaQuestionCircle,
  FaRegIdCard,
  FaShieldAlt,
  FaTv,
} from "react-icons/fa";
import { toast } from "sonner";

import { useUIStateStore } from "@/store/uiStateStore";

import IconDashboard from "../images/ic_dashboard.png";
import IconGigs from "../images/ic_gigs.png";
import IconOpportunities from "../images/ic_opportunities.png";
import IconSongs from "../images/ic_songs.png";
import IconWriters from "../images/ic_writers.png";
import IconErrorWarningFill from "../images/rebrandIcons/warningIcon";
import useAppStore, { persistAccountToLocalStorage } from "../store/appStateZustand";
import NavItem from "./NavItem";

const IconStatements = <FaFileAlt />;
const IconRoyalties = <FaChartLine />;
const IconSecurity = <FaRegIdCard />;
const IconPrivacyPolicy = <FaShieldAlt />;
const IconCookiesPolicy = <FaCookie />;
const IconSyncs = <FaTv />;
const IconBroadcasts = <FaBroadcastTower />;
const IconSettings = <FaCog />;
const IconFaq = <FaQuestionCircle />;
const IconArrowLeft = <FaArrowAltCircleLeft />;
const IconDatasuite = <FaDatabase />;

function SidebarNavItems({
  darkColour,
  highlightColour,
  siteConfig,
  hideRoyalties,
  accounts,
  selectedAccount,
  isAccountSwitching,
}) {
  const { t } = useTranslation(["other", "common"]);
  const navItemsEnabled = useAppStore(s => s.navItemsEnabled);
  const kyc = useAppStore(s => s.kyc);
  const showKyc = kyc?.kycrequired && !kyc?.kycpassed;
  const isTunecore = useUIStateStore(state => state.isTunecore);
  const isRoughTrade = siteConfig?.display_name?.includes("Rough Trade Publishing");
  const isCookingVinyl = siteConfig?.display_name?.includes("Cooking Vinyl Publishing");
  const flags = useFlags();
  const enableSplitSideNav = flags["pd-show-split-sidenav"];
  const enableDatasuite = flags["pd-show-datasuite-navitem"];

  // Handler for navigating to Datasuite (rights-app) with SSO authentication
  const handleDatasuiteNavigation = async () => {
    try {
      const accessToken = useAppStore.getState().authentication?.access_token;

      const apiUrl = import.meta.env.VITE_API;
      const response = await fetch(`${apiUrl}/rightsapp/ssoExchange`, {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Failed to get SSO token");
      }

      const data = await response.json();
      if (data.ssoToken) {
        const rightsAppUrl = import.meta.env.VITE_RIGHTS_APP_URL;
        const returnUrl = encodeURIComponent(window.location.origin);
        window.open(`${rightsAppUrl}?token=${data.ssoToken}&returnUrl=${returnUrl}`, "_blank");
      }
      else {
        throw new Error("No SSO token received");
      }
    }
    catch (error) {
      console.error("Datasuite navigation error:", error);
      toast.error("Failed to navigate to Datasuite. Please try again.");
    }
  };

  const kycRequired = kyc?.kycrequired && !kyc?.kycpassed;
  const warningIcon = (
    <div className="ml-2">
      <IconErrorWarningFill fill={highlightColour} />
    </div>
  );

  // Determine if we should show organized sections (only if user has multiple artist accounts)
  const hasMultipleAccounts = accounts?.artistaccounts && accounts.artistaccounts.length > 1;
  // Rough Trade should always use flat navigation regardless of account count
  const showOrganizedSections = hasMultipleAccounts && !isRoughTrade && enableSplitSideNav;

  // Global pages - show data for all artist accounts regardless of selectedAccount value
  const globalNavItems = [
    {
      key: "dashboard",
      enabled: !siteConfig?.disable_dashboard,
      component: (
        <NavItem
          exact
          to="/"
          title={t("other:nav-dashboard")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconDashboard}
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "royalties",
      enabled: !siteConfig?.disable_royalties && !hideRoyalties,
      component: (
        <NavItem
          to="/royalties"
          title={t("other:nav-royalties")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconRoyalties}
          iconIsFA
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "settings",
      enabled: true, // Settings is always enabled
      component: (
        <NavItem
          lighterBackground
          to="/settings"
          title={t("other:nav-settings")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconSettings}
          iconIsFA
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "datasuite",
      enabled: enableDatasuite,
      component: (
        <NavItem
          externalLink
          title={t("other:nav-datasuite", "Datasuite")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconDatasuite}
          iconIsFA
          disabled={!navItemsEnabled}
          onClick={handleDatasuiteNavigation}
        />
      ),
    },
  ];

  // Artist account-specific pages - show data for specific selectedAccount artist accounts
  const artistAccountNavItems = [
    {
      key: "songs",
      enabled: !siteConfig?.disable_songs,
      component: (
        <NavItem
          to="/songs"
          title={t("other:nav-songs")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconSongs}
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "writers",
      enabled: !siteConfig?.disable_writers,
      component: (
        <NavItem
          to="/writers"
          title={t("other:nav-writers")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconWriters}
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "gigs",
      enabled: !siteConfig?.disable_gigs,
      component: (
        <NavItem
          to="/gigs"
          title={t("other:nav-gigs")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconGigs}
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "opportunities",
      enabled: !siteConfig?.disable_opportunities,
      component: (
        <NavItem
          to="/opportunities"
          title={t("other:nav-opportunities")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconOpportunities}
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "syncs",
      enabled: !siteConfig?.disable_syncs,
      component: (
        <NavItem
          to="/syncs"
          title={t("other:nav-syncs")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconSyncs}
          iconIsFA
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "statements",
      enabled: !siteConfig?.disable_statements && !hideRoyalties,
      component: (
        <NavItem
          lighterBackground
          to="/statements"
          title={t("other:nav-statements")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconStatements}
          iconIsFA
          disabled={!navItemsEnabled}
        />
      ),
    },
    {
      key: "broadcasts",
      enabled: !siteConfig?.disable_broadcasts,
      component: (
        <NavItem
          to="/broadcasts"
          title={t("other:nav-broadcasts")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconBroadcasts}
          iconIsFA
          disabled={!navItemsEnabled}
        />
      ),
    },
  ];

  return (
    <div className="flex flex-col space-y-1">
      {/* Security KYC warning at top if needed */}
      {showKyc && (
        <NavItem
          exact
          to="/security"
          title={t("other:nav-security")}
          highlightColour={highlightColour}
          darkColour={darkColour}
          icon={IconSecurity}
          iconIsFA
          notification={kycRequired ? warningIcon : undefined}
        />
      )}

      {/* TuneCore back link */}
      {isTunecore && (
        <NavItem
          externalLink
          linkAddress="https://web.tunecore.com/dashboard"
          title={t("other:back-to-tunecore")}
          icon={IconArrowLeft}
          highlightColour={highlightColour}
          darkColour={darkColour}
          iconIsFA
        />
      )}

      {showOrganizedSections ? (
        // Organized sections for users with multiple accounts
        <>
          {/* Global Pages Section */}
          <div className="space-y-1">
            <Text as="subtitle" className="px-4 py-3 text-sm text-ink-secondary">
              {t("common:general-pages", "General (All Accounts)")}
            </Text>
            {globalNavItems.map(item =>
              item.enabled ? <div key={`global-${item.key}`}>{item.component}</div> : null,
            )}
          </div>

          {/* Separator */}
          <div className="h-px mx-4 my-4 bg-layer-raised opacity-30"></div>

          {/* Artist Account Specific Section */}
          <div className="space-y-1">
            <Text as="subtitle" className="px-4 py-3 text-sm text-ink-secondary">
              {t("common:selected-account-pages", "Selected Account")}
            </Text>

            {/* Account Switcher */}
            {accounts?.artistaccounts && accounts.artistaccounts.length > 0 && (
              <div className="px-4 pb-3 space-y-2">
                {accounts.artistaccounts.length > 1 && (
                  <Combobox
                    data={accounts.artistaccounts
                      .sort((a, b) => a.accountname.localeCompare(b.accountname))
                      .map(account => ({
                        value: account.artistaccountcode,
                        label: `${account.accountname}\n${account.artistaccountcode}`,
                      }))}
                    value={selectedAccount}
                    onValueChange={async (value) => {
                      const selectedArtistAccount = accounts.artistaccounts.find(
                        account => account.artistaccountcode === value,
                      );
                      if (selectedArtistAccount) {
                        try {
                          // Set loading state via Zustand store
                          useAppStore.getState().setAccountSwitching(true);

                          // Update account state directly via Zustand store
                          useAppStore.setState({
                            selectedAccount: selectedArtistAccount.artistaccountcode,
                            selectedCatalogue: selectedArtistAccount.cataloguecode,
                          });

                          // Persist to localStorage for cross-session recovery
                          persistAccountToLocalStorage(selectedArtistAccount.artistaccountcode);

                          toast.success(`Switched to ${selectedArtistAccount.accountname}`, {
                            description: `Account: ${selectedArtistAccount.artistaccountcode}`,
                            duration: 3000,
                          });
                        }
                        catch (error) {
                          console.error("Account switch failed:", error);
                          toast.error("Failed to switch account", {
                            description: "Please try again",
                            duration: 4000,
                          });
                        }
                        finally {
                          // Clear loading state
                          useAppStore.getState().setAccountSwitching(false);
                        }
                      }
                    }}
                    placeholder={isAccountSwitching ? t("common:switching", "Switching...") : t("common:switch-accounts", "Switch accounts")}
                    searchPlaceholder={t("common:search-accounts-or-codes", "Search accounts or codes...")}
                    emptyText={t("common:no-accounts-found", "No accounts found.")}
                    disabled={isAccountSwitching}
                    searchByLabel
                    isNav
                    contentClassName="w-full"
                    className="w-full"
                    textAlign="left"
                    renderOption={(option) => {
                      const [accountName, accountCode] = option.label.split("\n");
                      return (
                        <div className="flex flex-col text-left">
                          <span className="font-medium">{accountName}</span>
                          <span className="text-xs opacity-75">{accountCode}</span>
                        </div>
                      );
                    }}
                  />
                )}
              </div>
            )}

            {artistAccountNavItems.map(item =>
              item.enabled ? <div key={`artist-${item.key}`}>{item.component}</div> : null,
            )}
          </div>
        </>
      ) : (
        // Flat navigation for users with single or no accounts
        <div className="space-y-1">
          {/* Show all navigation items in a flat structure, excluding settings */}
          {globalNavItems
            .filter(item => item.key !== "settings")
            .map(item =>
              item.enabled ? <div key={`flat-global-${item.key}`}>{item.component}</div> : null,
            )}
          {artistAccountNavItems.map(item =>
            item.enabled ? <div key={`flat-artist-${item.key}`}>{item.component}</div> : null,
          )}
        </div>
      )}

      {/* Legal and Info Section */}
      <div className="space-y-1">
        {/* Only show section header and separator if we're using organized sections */}
        {showOrganizedSections && (
          <>
            {/* Separator */}
            <div className="h-px mx-4 my-4 bg-layer-raised opacity-30"></div>
            <Text as="subtitle" className="px-4 py-3 text-sm text-ink-secondary">
              {t("common:info-pages", "Info")}
            </Text>
          </>
        )}

        {/* Settings item - only show in flat navigation (moved here from global items) */}
        {!showOrganizedSections
          && globalNavItems.find(item => item.key === "settings")?.enabled && (
          <div key="flat-settings">
            {globalNavItems.find(item => item.key === "settings").component}
          </div>
        )}

        {!isTunecore && (
          <NavItem
            to="/privacy-policy"
            title={t("other:nav-privacy-policy")}
            highlightColour={highlightColour}
            darkColour={darkColour}
            icon={IconPrivacyPolicy}
            iconIsFA
            disabled={!navItemsEnabled}
          />
        )}

        {!isTunecore && (
          <NavItem
            externalLink
            linkAddress="https://sentricmusic.com/cookies-policy/"
            title="Cookies Policy"
            highlightColour={highlightColour}
            darkColour={darkColour}
            icon={IconCookiesPolicy}
            iconIsFA
            disabled={!navItemsEnabled}
          />
        )}

        {(isRoughTrade || isCookingVinyl) && (
          <NavItem
            to="/faq"
            title={t("other:faq")}
            highlightColour={highlightColour}
            darkColour={darkColour}
            icon={IconFaq}
            iconIsFA
            disabled={!navItemsEnabled}
          />
        )}
      </div>
    </div>
  );
}

export default SidebarNavItems;
